/**
 * Risk Assessment Slice
 * 
 * Manages risk assessment data with caching
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Cycle, Objective, CompliancePurpose, Assertion, RubricItem, normalizeRiskRanking } from '../../services/riskAssessmentService';
import { resetReduxState } from '../actions';
import { RiskRanking } from '@/services/types';

/**
 * Qualitative Factor Type (for Tab 1)
 */
export interface QualitativeFactor {
  id: string;
  qualitativeFactor: string;
  riskRanking: RiskRanking;
  customComment: string;
  isFixed?: boolean; // For Fraud Risk and Overall Conclusion
  displayOrder?: number;
}

/**
 * Assessment Details Type (for Tab 1 and Tab 3)
 */
export interface AssessmentDetails {
  assessmentId: string;
  quantitativeAssessment: string;
  overallRiskRanking: RiskRanking | null;
  overallRiskDescription: string;
  incentives: string;
  opportunities: string;
  rationalizations: string;
  fraudRiskRanking: RiskRanking | null;
  fraudRiskDescription: string;
}

export interface RiskAssessmentState {
  // Risk assessment cycles data for current annual report
  cycles: Cycle[];
  lastFetchedReportId: string | null;
  
  // Current assessment details (shared by Tab 1 and Tab 3)
  currentAssessmentDetails: AssessmentDetails | null;
  
  // Manual qualitative factors from API (Tab 1) - excludes fixed rows
  manualQualitativeFactors: QualitativeFactor[];
  
  // Objectives from API (Tab 2) - shared with Tab 3 for picker
  objectives: Objective[];
  
  // Compliance Purposes from API (Tab 3)
  compliancePurposes: CompliancePurpose[];
  
  // Assertions (for Tab 2 Objective drawer)
  assertions: Assertion[];
  
  // Rubric Items (for Tab 4 - Risk Matrix Configuration)
  likelihoodRubricItems: RubricItem[];
  severityRubricItems: RubricItem[];
}

const initialState: RiskAssessmentState = {
  cycles: [],
  lastFetchedReportId: null,
  currentAssessmentDetails: null,
  manualQualitativeFactors: [],
  objectives: [],
  compliancePurposes: [],
  assertions: [],
  likelihoodRubricItems: [],
  severityRubricItems: [],
};

// Helper function to find and update riskAssessment in cycles
const findAndUpdateRiskAssessment = (
  cycles: Cycle[],
  assessmentId: string,
  updater: (riskAssessment: Cycle['riskAssessments'][0]) => void
): void => {
  for (const cycle of cycles) {
    const riskAssessment = cycle.riskAssessments.find(ra => ra.id === assessmentId);
    if (riskAssessment) {
      updater(riskAssessment);
      break;
    }
  }
};

const riskAssessmentSlice = createSlice({
  name: 'riskAssessment',
  initialState,
  reducers: {
    // Set risk assessment cycles data
    setRiskAssessmentCycles: (
      state,
      action: PayloadAction<{ cycles: Cycle[]; reportId: string }>
    ) => {
      state.cycles = action.payload.cycles;
      state.lastFetchedReportId = action.payload.reportId;
    },

    // Set current assessment details and sync riskRanking/fraudRiskRank with cycles
    setAssessmentDetails: (state, action: PayloadAction<AssessmentDetails | null>) => {
      state.currentAssessmentDetails = action.payload;
      
      // If payload is null, just clear the details and return
      if (!action.payload) {
        return;
      }
      
      // Sync riskRanking and fraudRiskRank with the corresponding riskAssessment in cycles
      const { assessmentId, overallRiskRanking, fraudRiskRanking } = action.payload;
      
      findAndUpdateRiskAssessment(state.cycles, assessmentId, (riskAssessment) => {
        // Update riskRanking from overallRiskRanking
        if (overallRiskRanking) {
          riskAssessment.riskRanking = normalizeRiskRanking(overallRiskRanking);
        }
        
        // Update fraudRiskRank from fraudRiskRanking
        if (fraudRiskRanking) {
          riskAssessment.fraudRiskRank = normalizeRiskRanking(fraudRiskRanking);
        }
      });
    },

    // Set manual qualitative factors (from API, excludes fixed rows)
    setManualQualitativeFactors: (state, action: PayloadAction<QualitativeFactor[]>) => {
      state.manualQualitativeFactors = action.payload;
    },

    // Set objectives (from API, Tab 2) and sync objectivesCount with cycles
    setObjectives: (state, action: PayloadAction<Objective[]>) => {
      state.objectives = action.payload;
      
      // Sync objectivesCount with the corresponding riskAssessment in cycles
      if (state.currentAssessmentDetails) {
        const assessmentId = state.currentAssessmentDetails.assessmentId;
        
        findAndUpdateRiskAssessment(state.cycles, assessmentId, (riskAssessment) => {
          riskAssessment.objectivesCount = action.payload.length;
        });
      }
    },

    // Set compliance purposes (from API, Tab 3) and sync fraudRisksCount with cycles
    setCompliancePurposes: (state, action: PayloadAction<CompliancePurpose[]>) => {
      state.compliancePurposes = action.payload;
      
      // Sync fraudRisksCount with the corresponding riskAssessment in cycles
      if (state.currentAssessmentDetails) {
        const assessmentId = state.currentAssessmentDetails.assessmentId;
        
        findAndUpdateRiskAssessment(state.cycles, assessmentId, (riskAssessment) => {
          riskAssessment.fraudRisksCount = action.payload.length;
        });
      }
    },

    // Set assertions (from API, for Tab 2 drawer)
    setAssertions: (state, action: PayloadAction<Assertion[]>) => {
      state.assertions = action.payload;
    },

    // Set likelihood rubric items (from API, Tab 4)
    setLikelihoodRubricItems: (state, action: PayloadAction<RubricItem[]>) => {
      state.likelihoodRubricItems = action.payload;
    },

    // Set severity rubric items (from API, Tab 4)
    setSeverityRubricItems: (state, action: PayloadAction<RubricItem[]>) => {
      state.severityRubricItems = action.payload;
    },

    // Clear all risk assessment data
    clearRiskAssessmentData: (state) => {
      state.cycles = [];
      state.lastFetchedReportId = null;
      state.currentAssessmentDetails = null;
      state.manualQualitativeFactors = [];
      state.objectives = [];
      state.compliancePurposes = [];
      state.assertions = [];
      state.likelihoodRubricItems = [];
      state.severityRubricItems = [];
    },

    // Clear compliance purposes cache (to force refetch in Tab 3)
    clearCompliancePurposes: (state) => {
      // console.log('🔄 Clearing compliance purposes cache...');
      state.compliancePurposes = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetReduxState, () => initialState);
  },
});

export const {
  setRiskAssessmentCycles,
  setAssessmentDetails,
  setManualQualitativeFactors,
  setObjectives,
  setCompliancePurposes,
  setAssertions,
  setLikelihoodRubricItems,
  setSeverityRubricItems,
  clearRiskAssessmentData,
  clearCompliancePurposes,
} = riskAssessmentSlice.actions;

export default riskAssessmentSlice.reducer;

